/**
 * @author      sammy.dowds@zumper.com (Sammy Dowds)
 * @copyright   Copyright (c) 2023, Zumper
 */

import { voidEvent } from '@zcode/nr-utils'
import type { CardBackResponse } from '@zcode/state-type'
import React, {
  type KeyboardEvent,
  type MouseEvent,
  type MouseEventHandler,
  type Ref,
  forwardRef,
  useState,
  startTransition,
} from 'react'
import type { O } from 'ts-toolbelt'
import {
  ListingCardCheckboxOrCloseButton,
  type ListingCardCheckboxOrCloseButtonProps,
} from './ListingCardCheckboxOrCloseButton'
import {
  ListingCardContactSection,
  type ListingCardContactSectionProps,
} from './ListingCardContactSection'
import {
  ListingCardContentSection,
  type ListingCardContentSectionProps,
} from './ListingCardContentSection'
import {
  type ImageContainerProps,
  ListingCardImageSection,
  type ListingCardImageSectionProps,
} from './ListingCardImageSection'
import { ListingCardInfoSection, type ListingCardInfoSectionProps } from './ListingCardInfoSection'
import { ListingCardSM, type ListingCardSmProps as _ListingCardSmProps } from './ListingCardSm'
import * as styles from './ListingCardStyles'
import { ListingCardSizes } from './constants'
import { fetchCardBackData } from './helpers/fetchCardBackData'
import { useCardBackTextManagement } from './hooks/useCardBackTextManagement'
import { useListingCardStyles } from './useListingCardStyles'

interface BaseListingCardProps {
  onClickQuickLook?: (
    event: KeyboardEvent<HTMLButtonElement> | MouseEvent<HTMLButtonElement>
  ) => void
  withBorder?: boolean
  withBoxShadow?: boolean
}

/**
 * Adding a new ListingCard prop?
 *
 * First, put it in the props interface for the component that will ultimately use it.
 * (such as ListingCardInfoSection or ListingCardContentSection)
 * Then, see whether it's used in size sm cards or size md & lg cards, or both.
 * If it's only used in one size category, you need to add it to the list of "never"s
 * for the other. (And make sure not to pass that prop to a component with the wrong size!)
 */

/** The props for web-zumper-ui's `<ListingCard size={SM} />`. You probably want **NzrListingCardSmProps** instead */
export type ListingCardSmProps = BaseListingCardProps &
  O.Omit<
    _ListingCardSmProps,
    'cardBorder' | 'onDoubleClick' | 'isShortTerm' | 'showBorder' | 'showBoxShadow'
  > & {
    /** has no effect on small cards */
    cardBackData?: never
    /** has no effect on small cards */
    cardBackDataHasLoaded?: never
    /** has no effect on small cards */
    cardContainerProps?: never
    /** has no effect on small cards */
    cardContentContainerProps?: never
    /** has no effect on small cards */
    imageProps?: never
    /** has no effect on small cards */
    currentTab?: never
    /** has no effect on small cards */
    analyticChannel?: never
    /** has no effect on small cards */
    forceDesktop?: never
    /** has no effect on small cards */
    forceIntersected?: never
    /** has no effect on small cards */
    forceMobile?: never
    /** has no effect on small cards */
    isBot?: never
    /** has no effect on small cards */
    isSpotlightVariation?: never
    /** has no effect on small cards */
    isLargeSpotlight?: never
    /** has no effect on small cards */
    hideCTAs?: boolean
    /** has no effect on small cards */
    hideQuickLook?: never
    isShortTerm?: boolean | undefined
    /** has no effect on small cards */
    now?: never
    /** has no effect on small cards */
    onDetailClick?: never
    /** has no effect on small cards */
    onPaidPhoneClick?: never
    /** has no effect on small cards */
    onMouseEnter?: never
    /** has no effect on small cards */
    onMouseLeave?: never
    /** has no effect on small cards */
    onShortTermClick?: never
    /** has no effect on small cards */
    origin?: never
    /** has no effect on small cards */
    originTour?: never
    /** has no effect on small cards */
    paidPhoneMixpanelOrigin?: never
    /** has no effect on small cards */
    paidPhoneTraktorEventName?: never
    /** has no effect on small cards */
    parentName?: never
    /** has no effect on small cards */
    shouldLimitLeads?: never
    /** has no effect on small cards */
    shouldPreloadImage?: never
    /** has no effect on small cards */
    showPropertyBadge?: never
    size: ListingCardSizes.SM
    /** has no effect on small cards */
    DetailPageLink?: never
    /** has no effect on small cards */
    FavoriteContainer?: never
    /** has no effect on small cards */
    ListingCardMessageButton?: never
    /** has no effect on small cards */
    ListingCardTourButton?: never
    /** has no effect on small cards */
    ListCardBadges?: never
    /** has no effect on small cards */
    updatedAmenities?: never
    isCity?: boolean
    isHood?: boolean
    isVertical?: boolean
    shouldShowDefaultInfo?: boolean
  }

type ListingCardChildren = O.Omit<
  ListingCardInfoSectionProps & ListingCardImageSectionProps & ListingCardContentSectionProps,
  | 'handleQuickLookClick'
  | 'imageContainerProps'
  | 'isLargeCard'
  | 'isShortTerm'
  | 'showBorder'
  | 'showCheckbox'
  | 'showBoxShadow'
  | 'showInfo'
  | 'showPropertyBadge'
  | 'isCity'
  | 'isHood'
  | 'isVertical'
  | 'shouldShowDefaultInfo'
>
/** The props for web-zumper-ui's `<ListingCard size={MD | LG} />`. You probably want **NzrListingCardMdLgProps** instead */
export type ListingCardMdLgProps = BaseListingCardProps &
  ListingCardChildren & {
    cardBackData?: CardBackResponse
    cardContainerProps?: styles.CardContainerProps
    cardContentContainerProps?: styles.CardContentContainerProps
    imageProps?: ImageContainerProps
    forceDesktop?: boolean
    forceMobile?: boolean
    hideQuickLook?: boolean
    isBot: boolean
    isShortTerm?: boolean
    onClick?: MouseEventHandler<HTMLDivElement>
    onMouseEnter?: MouseEventHandler<HTMLDivElement>
    onMouseLeave?: MouseEventHandler<HTMLDivElement>
    /** defaults to true */
    showPropertyBadge?: boolean
    size: Exclude<ListingCardSizes, ListingCardSizes.SM>
  } & ListingCardCheckboxOrCloseButtonProps &
  // We don't show ListingCardContactSection if `isChecked` is a boolean, so don't require/allow
  // passing props that are only used by ListingCardContactSection in that case
  O.Omit<
    | (({ hideCTAs?: true; isChecked: boolean } | { hideCTAs: true; isChecked?: undefined }) & {
        [Key in Exclude<keyof ListingCardContactSectionProps, keyof ListingCardChildren>]?: never
      })
    | ({
        hideCTAs?: false | undefined
        isChecked?: undefined
      } & ListingCardContactSectionProps),
    'isLargeCard' | 'isShortTerm' | 'showBorder'
  > &
  // If isChecked and isShortTerm, we require onDetailClick, so that the "Details" link will open
  // the detail page, rather than just toggling the checkbox (like clicking elsewhere on the card does)
  (
    | {
        isChecked: boolean
        isShortTerm: true
        onDetailClick: (e: KeyboardEvent<HTMLButtonElement> | MouseEvent<HTMLButtonElement>) => void
      }
    | {
        isChecked?: undefined
        isShortTerm?: boolean
        onDetailClick?: (
          e: KeyboardEvent<HTMLButtonElement> | MouseEvent<HTMLButtonElement>
        ) => void
      }
    | {
        isChecked: boolean
        isShortTerm?: false
        onDetailClick?: (
          e: KeyboardEvent<HTMLButtonElement> | MouseEvent<HTMLButtonElement>
        ) => void
      }
  )

/** The props for web-zumper-ui's `ListingCard`. You probably want **NzrListingCardProps** instead */
export type ListingCardProps = ListingCardSmProps | ListingCardMdLgProps

type PropsProvidedByNzr =
  | 'Carousel'
  | 'DetailPageLink'
  | 'FavoriteContainer'
  | 'ListCardRating'
  | 'ListingCardMessageButton'
  | 'ListingCardTourButton'
  | 'ListCardBadges'
  | 'getCommonEntityData'
  | 'getHoodPropertyText'
  | 'isBot'
  | 'now'
  | 'onPaidPhoneClick'
  | 'parentName'
  | 'shouldLimitLeads'
  | 'updatedAmenities'

/** The props for nzr's `ListingCard` (which is passed contextually to other packages) */
export type NzrListingCardProps = O.Omit<ListingCardProps, PropsProvidedByNzr> &
  Partial<O.Pick<ListingCardProps, 'parentName' | 'size'>>
/** The props for nzr's `<ListingCard size={MD | LG} />` (which is passed contextually to other packages) */
export type NzrListingCardMdLgProps = O.Omit<ListingCardMdLgProps, PropsProvidedByNzr> &
  Partial<O.Pick<ListingCardProps, 'parentName' | 'size'>>
/** The props for nzr's `<ListingCard size={SM} />` (which is passed contextually to other packages) */
export type NzrListingCardSmProps = O.Omit<ListingCardSmProps, PropsProvidedByNzr> &
  Partial<O.Pick<ListingCardProps, 'parentName' | 'size'>>

function onDoubleClick(e: MouseEvent<HTMLDivElement>) {
  e.preventDefault()
  e.stopPropagation()
}

export const ListingCard = forwardRef(
  (
    {
      cardContainerProps,
      cardContentContainerProps,
      Carousel,
      currentTab,
      entity,
      forceDesktop,
      forceIntersected,
      forceMobile,
      getCommonEntityData,
      getHoodPropertyText,
      hideQuickLook,
      imageProps,
      isBot,
      isShortTerm = false,
      ListCardRating,
      onClickQuickLook,
      onMouseEnter,
      onMouseLeave,
      parentName,
      shouldPreloadImage,
      showPropertyBadge = true,
      testId,
      updatedAmenities,
      withBorder = true,
      withBoxShadow = false,
      ...rest
    }: ListingCardProps & { testId?: string },
    ref: Ref<HTMLDivElement>
  ) => {
    const [showInfo, setShowInfo] = useState(false)
    const [cardBackData, setCardBackData] = useState({} as CardBackResponse)
    const [cardBackDataHasLoaded, setCardBackDataHasLoaded] = useState(false)

    const { isCity, isHood, isVertical, shouldFetchCardBack, shouldShowDefaultInfo } =
      useCardBackTextManagement()

    const handleQuickLookClick = (e: MouseEvent<HTMLButtonElement>) => {
      if (shouldFetchCardBack && !showInfo && !cardBackDataHasLoaded) {
        fetchCardBackData(entity, setCardBackData, setCardBackDataHasLoaded)
      }
      voidEvent(e)
      !showInfo && onClickQuickLook?.(e)
      setShowInfo(!showInfo)
    }

    const isLargeCard = rest.size === ListingCardSizes.LG
    const showBorder = !isLargeCard && (withBoxShadow || withBorder)
    const showBoxShadow = showBorder && withBoxShadow
    const isSingleMargin = showBorder && rest.isChecked === false
    const { cardHeight, cardBorder, cardBorderRadius, bodyHeight, bodyPadding } =
      useListingCardStyles(
        rest.size,
        forceDesktop,
        forceMobile,
        rest.hideCTAs,
        rest.isChecked,
        showBorder
      )

    if (rest.size === ListingCardSizes.SM) {
      return (
        <ListingCardSM
          {...rest}
          Carousel={Carousel}
          ListCardRating={ListCardRating}
          cardBorder={cardBorder}
          entity={entity}
          getCommonEntityData={getCommonEntityData}
          getHoodPropertyText={getHoodPropertyText}
          isShortTerm={isShortTerm}
          onDoubleClick={onDoubleClick}
          showBorder={showBorder}
          showBoxShadow={showBoxShadow}
        />
      )
    }

    return (
      // biome-ignore lint/a11y/useKeyWithClickEvents: <explanation>
      <div
        css={styles.listingCardContainer(
          cardHeight,
          cardBorder,
          cardBorderRadius,
          showBoxShadow,
          isSingleMargin,
          cardContainerProps
        )}
        data-testid={testId}
        onClick={(e) => {
          startTransition(() => {
            rest.onClick?.(e)
          })
        }}
        onDoubleClick={onDoubleClick}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        ref={ref}
      >
        <ListingCardImageSection
          Carousel={Carousel}
          FavoriteContainer={rest.FavoriteContainer}
          ListCardBadges={rest.ListCardBadges}
          entity={entity}
          forceDesktop={forceDesktop}
          forceIntersected={forceIntersected}
          imageContainerProps={imageProps}
          now={rest.now}
          parentName={parentName}
          shouldPreloadImage={shouldPreloadImage}
          showBorder={showBorder}
          showCheckbox={typeof rest.isChecked === 'boolean'}
          showPropertyBadge={showPropertyBadge}
          size={rest.size}
        />
        <ListingCardCheckboxOrCloseButton {...rest} />
        <div
          css={styles.listingCardContentContainer(
            bodyHeight,
            bodyPadding,
            cardContentContainerProps
          )}
        >
          {showInfo ? (
            <ListingCardInfoSection
              cardBackData={cardBackData}
              cardBackDataHasLoaded={cardBackDataHasLoaded}
              entity={entity}
              forceMobile={forceMobile}
              handleQuickLookClick={handleQuickLookClick}
              isCity={isCity}
              isHood={isHood}
              isShortTerm={isShortTerm}
              isVertical={isVertical}
              shouldShowDefaultInfo={shouldShowDefaultInfo}
              updatedAmenities={updatedAmenities}
            />
          ) : (
            <ListingCardContentSection
              DetailPageLink={rest.DetailPageLink}
              ListCardRating={ListCardRating}
              entity={entity}
              forceDesktop={forceDesktop}
              forceMobile={forceMobile}
              getCommonEntityData={getCommonEntityData}
              getHoodPropertyText={getHoodPropertyText}
              handleQuickLookClick={handleQuickLookClick}
              hideQuickLook={hideQuickLook}
              isLargeCard={isLargeCard}
              isShortTerm={isShortTerm}
              onDetailClick={rest.onDetailClick}
              showInfo={showInfo}
              updatedAmenities={updatedAmenities}
            />
          )}
          {isBot && (
            <div css={styles.bot}>
              <ListingCardInfoSection
                cardBackDataHasLoaded
                entity={entity}
                forceMobile={forceMobile}
                handleQuickLookClick={handleQuickLookClick}
                isCity={isCity}
                isHood={isHood}
                isShortTerm={isShortTerm}
                isVertical={isVertical}
                shouldShowDefaultInfo={shouldShowDefaultInfo}
              />
            </div>
          )}
          {typeof rest.isChecked !== 'boolean' && !rest.hideCTAs && (
            <ListingCardContactSection
              ListingCardMessageButton={rest.ListingCardMessageButton}
              ListingCardTourButton={rest.ListingCardTourButton}
              analyticChannel={rest.analyticChannel}
              currentTab={currentTab}
              entity={entity}
              forceDesktop={forceDesktop}
              forceMobile={forceMobile}
              isLargeCard={isLargeCard}
              isShortTerm={isShortTerm}
              onDetailClick={rest.onDetailClick}
              onPaidPhoneClick={rest.onPaidPhoneClick}
              onShortTermClick={rest.onShortTermClick}
              origin={rest.origin}
              originTour={rest.originTour}
              paidPhoneMixpanelOrigin={rest.paidPhoneMixpanelOrigin}
              shouldLimitLeads={rest.shouldLimitLeads}
              showBorder={showBorder}
            />
          )}
        </div>
      </div>
    )
  }
)
